import axios from "axios";
import * as Helpers from "@/helpers";

/**
 * Common helper function to execute a query and process its results
 * @param {Object} context - The Vuex context object
 * @param {Object} query - The query to execute
 * @param {Object} options - Configuration options
 * @param {boolean} options.addRows - Whether to add rows to existing results
 * @param {string} options.moduleName - The module name (e.g. 'Programs', 'Campaigns', 'Channels', 'Streaming')
 * @param {Object} options.mutations - The mutations object for the module
 * @param {Object} options.getters - The getters object for the module
 * @param {Function} options.executeQueryFn - The API function to execute the query
 * @param {Function} options.processRowFn - Optional function to process each row
 */
export const executeQueryHelper = async (context, query, options) => {
    const {
        addRows = false,
        moduleName,
        mutations,
        executeQueryFn,
        processRowFn = (row) => ({ ...row, selected: false })
    } = options;

    // Update queryForAPI for easier debugging
    context.commit(mutations[`set${moduleName}QueryForAPI`], query);
    context.commit(mutations[`set${moduleName}Results`], {
        ...context.state[`${moduleName.toLowerCase()}Results`],
        loadingTable: true
    });


    try {
        const r = await executeQueryFn(query);

        // Check if we got any rows
        if (r.metadata[0].TotalRows == 0) {
            context.commit(mutations[`set${moduleName}Results`], {
                status: "EMPTY_RESULT"
            });
            return;
        }

        const metadata = r.metadata[0];

        // Create column objects from display columns and measures
        let columnObjects = query.displayColumns.concat(query.measures);
        let columnsInfo = [
            ...context.getters[`get${moduleName}ColumnTypes`],
            ...context.getters[`get${moduleName}Measures`]
        ];
        columnObjects = columnObjects.map(column => {
            let columnInfo = columnsInfo.find(
                c => c.column_type == column.column_type
            );
            return {
                isMeasure: query.measures
                    .map(m => m.display_name)
                    .includes(column.display_name),
                column_type: column.column_type,
                key: column.display_name,
                label: column.display_name,
                sortable: true,
                isNumber: columnInfo.isNumber,
                isPercentage: columnInfo.isPercentage,
                isWeekday: columnInfo.isWeekday,
                default_aggregation: columnInfo.default_aggregation,
                allowedAggregations: columnInfo.allowedAggregations,
                tooltip: columnInfo.tooltip,
                tdClass: columnInfo.tdClass,
                thClass: columnInfo.thClass
            };
        });
        // Handle row filtering for pagination
        let filteredTable;
        if (context.state[`${moduleName.toLowerCase()}Results`].status == "LOADED" && addRows) {
            filteredTable = r.table.slice(1);
        } else {
            filteredTable = r.table;
        }

        // Apply module-specific row processing
        if (moduleName === 'Channels') {
            filteredTable.forEach(c => {
                if (
                    context.getters.getChannelsChannelHouses
                        .map(ch => ch.orig_code)
                        .includes(c.ChannelKey)
                ) {
                    c.Kanalnamn = `${c.Kanalnamn} (hela TV-huset)`;
                }
            });
        }

        // Process summary data if available
        let summaryObject = {};
        if (r.summary) {
            for (let i = 0; i < r.summary.length; i++) {
                Object.keys(r.summary[i]).reduce(function (result, key) {
                    let val = r.summary[i][key];
                    if (!isNaN(val) && val) {
                        if (val < 10)
                            r.summary[i][key] = val.toLocaleString("fr", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            });
                        else r.summary[i][key] = Math.round(val).toLocaleString("fr");
                    }
                }, {});
            }
            summaryObject.sumAvgArray = r.summary.filter(
                r => r.type === "SUM" || r.type === "AVERAGE"
            );
            summaryObject.array = r.summary;
            context.commit(mutations[`set${moduleName}SumVisible`], false);

        } else {
            summaryObject.array = [];
        }

        // Get runtime config and setup aggregation dropdowns
        let config = await axios.get("./config/runtime.json").then(conf => conf.data);
        let aggregationTypeDropdowns =
            context.getters[`get${moduleName}Results`].data === null
                ? []
                : context.getters[`get${moduleName}Results`].data.aggregationTypeDropdowns;

        if (aggregationTypeDropdowns.length === 0) {
            let columnMeasures = columnObjects.filter(c => c.isMeasure);
            for (let i = 0; i < columnMeasures.length; i++) {
                aggregationTypeDropdowns.push({
                    key: columnMeasures[i].key,
                    value: columnMeasures[i].default_aggregation || "AVG",
                    allowedAggregations: columnMeasures[i].allowedAggregations?.map(x => ({
                        value: x,
                        text: x
                    })) || [],
                    left: "0px"
                });
            }
        }

        // Set default aggregation for measures
        for (let i = 0; i < columnObjects.length; i++) {
            if (columnObjects[i].isMeasure) {
                columnObjects[i].key = Helpers.replaceSuffix(
                    columnObjects[i],
                    columnObjects[i].default_aggregation || "AVG"
                );
                // Add streaming specific classes
                if (moduleName === 'Streaming') {
                    columnObjects[i].thClass = "measure-header";
                    columnObjects[i].tdClass = "measure-value";
                }
            }
        }

        // Commit the final results
        context.commit(mutations[`set${moduleName}Results`], {
            status: "LOADED",
            data: {
                aggregationTypeDropdowns: config.ShowGroupByDropdowns
                    ? aggregationTypeDropdowns
                    : [],
                rows: filteredTable.map(processRowFn),
                columns: columnObjects,
                summary: {
                    ...summaryObject
                },
                ...(moduleName === 'Streaming' ? {
                    perPage: 50,
                    currentPage: 1,
                    currentlySelectedRowIdx: null,
                    previouslySelectedRowIdx: null,
                    hasExcludedStream: false
                } : {})
            },
            totalRows: metadata.TotalRows,
            currentPage: 1,
            perPage: moduleName === 'Streaming' ? 50 : 100
        });

        // Update queryForAPI with pagination data
        context.commit(mutations[`set${moduleName}QueryForAPI`], {
            ...query,
            last_row_values: Object.keys(r.lastrow[0]).map(x => ({
                display_name: x,
                value: r.lastrow[0][x]
            }))
        });

    } catch (error) {
        console.log(error);
        context.commit(mutations[`set${moduleName}Results`], {
            status: "FAILED",
            data: null,
            error: error
        });
    }
};
